// src/hooks/useProfileEditor.js
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getAuth } from "firebase/auth";
import useUserProfileStore, {
  selectUserProfile,
  selectSetUserProfile,
  selectClearUserProfile,
} from "../store/useUserProfileStore";
import { clearSignedOutUserStorage } from "../store/appStorage";
import { useRenderTrace } from "../dev/renderProfiler";
import {
  getOrCreateUserProfile,
  fetchAzureUserProfile,
  updateProfileInFirestore,
  updateProfileImageInAllCollections,
  updateAzureUserProfileUsername,
  updateAzureUserProfilePicture,
  updateAzureUserProfile,
  checkUserNameAvailability,
  removeFcmTokenFromFirestore,
  updateAzureUserPhoneNumber,
} from "../services/profileService";

// Firestore (for admin-driven rewards programs)
import { db } from "../firebase";
import { deleteField, doc, getDoc, onSnapshot, updateDoc } from "firebase/firestore";
import { getDeviceId } from "../utils/deviceId";
import { PROFILE_TEXT_MAX_LENGTH, trimAndLimitProfileText } from "../utils/profileText";
import { sanitizeProfileDisplayNameInput } from "../utils/profileName";
import { persistProfileImageUpdate } from "../services/profileImageUpdateService";
import { buildProfileEditPayload } from "../utils/profileEditPayload";

export default function useProfileEditor() {
  const userProfile = useUserProfileStore(selectUserProfile);
  const setUserProfile = useUserProfileStore(selectSetUserProfile);
  const clearUserProfile = useUserProfileStore(selectClearUserProfile);

  const auth = getAuth();
  const user = auth.currentUser || null;
  const userId = user?.uid || null;

  const [isLoading, setIsLoading] = useState(false);
  const [azureUserProfile, setAzureUserProfile] = useState(null);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    userName: "",
    displayName: "",
    bio: "",
    profileImage: "",
    email: "",
    phoneNumber: "",
    affiliatedRewards: [], // [{ programKey, programName, memberId }]
  });

  const [userNameError, setUserNameError] = useState(false);
  const [userNameDisabled, setUserNameDisabled] = useState(false);
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [usernameAvailability, setUsernameAvailability] = useState(null);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [saveChangesLoading, setSaveChangesLoading] = useState(false);
  useRenderTrace("useProfileEditor", {
    userId,
    hasUserProfile: Boolean(userProfile),
    isLoading,
    checkingUsername,
    saveChangesLoading,
    snackbarOpen,
  });

  const usernameTimerRef = useRef(null);

  // Admin-configured rewards programs (from DB)
  const [affiliatePrograms, setAffiliatePrograms] = useState([]);

  const LOGGING_OUT_FLAG = "loggingOutNow";
  const clearUserSessionUiFlags = useCallback(() => {
    // Clears per-user UI flags so a different user signing in on the same tab
    // does not inherit "already shown" success dialogs.
    try {
      const keys = Object.keys(sessionStorage);
      keys.forEach((k) => {
        if (k.startsWith("hasShownSuccess-") || k.startsWith("hasShownSuccess")) {
          sessionStorage.removeItem(k);
        }
      });
    } catch (e) {
      console.warn("Failed to clear session success flags:", e);
    }
  }, []);

  useEffect(() => {
    const ref = doc(db, "config", "app");
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const data = snap.exists() ? snap.data() : {};
        const raw = Array.isArray(data?.rewardsPrograms) ? data.rewardsPrograms : [];
        const mapped = raw
          .filter((p) => p && typeof p.name === "string" && typeof p.value === "string")
          .map((p) => ({ key: p.value, label: p.name }));
        setAffiliatePrograms(mapped);
      },
      (err) => {
        console.error("Failed to load rewards programs:", err);
        setAffiliatePrograms([]); // fail safe
      }
    );
    return () => unsub();
  }, []);

  const updateUsernameFlags = useCallback((name) => {
    if (name?.includes("@")) {
      setUserNameError(true);
      setUserNameDisabled(false);
    } else {
      setUserNameError(false);
      setUserNameDisabled(true);
    }
  }, []);

  const setUserFormData = useCallback((profile, currentUser, azure = null) => {
    setFormData({
      userName: trimAndLimitProfileText(azure?.userName ?? profile?.userName ?? ""),
      displayName: sanitizeProfileDisplayNameInput(azure?.fullName ?? profile?.displayName ?? ""),
      bio: azure?.biography ?? profile?.bio ?? "",
      profileImage: profile?.profileImage ?? "",
      email: currentUser?.email ?? "",
      phoneNumber: currentUser?.phoneNumber ?? "",
      affiliatedRewards: Array.isArray(profile?.affiliatedRewards) ? profile.affiliatedRewards : [],
    });
  }, []);

  // Initial bootstrap
  useEffect(() => {
    const boot = async () => {
      const currentUser = getAuth().currentUser;
      if (!currentUser) return;

      setIsLoading(true);
      try {
        const baseProfile = await getOrCreateUserProfile(currentUser);
        setUserProfile(baseProfile);

        const azure = await fetchAzureUserProfile(baseProfile?.email).catch(() => null);
        setAzureUserProfile(azure);

        if (currentUser?.phoneNumber) {
          const updated = {
            ...baseProfile,
            phoneNumber: currentUser?.phoneNumber,
            uid: currentUser.uid,
          };

          // Update Firebase profile phone number if not match with firebase auth
          if (baseProfile?.phoneNumber !== currentUser?.phoneNumber) {
            await updateProfileInFirestore(currentUser.uid, {
              phoneNumber: currentUser.phoneNumber,
              uid: currentUser.uid,
            });
            setUserProfile(updated);
          }

          // Update Azure phone number if not match with firebase auth
          if (azure && azure?.phoneNumber !== currentUser?.phoneNumber) {
            await updateAzureUserPhoneNumber(currentUser?.phoneNumber, currentUser?.email);
          }
        }

        setUserFormData(baseProfile, currentUser, null);
        updateUsernameFlags(baseProfile?.userName || "");
      } catch (e) {
        console.error("Profile bootstrap failed:", e);
      } finally {
        setIsLoading(false);
      }
    };

    boot();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const currentUser = getAuth().currentUser;
    if (currentUser && userProfile) {
      setUserFormData(userProfile, currentUser, null);
      updateUsernameFlags(userProfile.userName || "");
    }
  }, [userProfile, azureUserProfile, setUserFormData, updateUsernameFlags]);

  const setField = useCallback(
    (name, value) => {
      if (name === "userName") {
        const cleaned = trimAndLimitProfileText(String(value || "").replace(/\s/g, ""));
        setFormData((prev) => ({ ...prev, [name]: cleaned }));

        updateUsernameFlags(cleaned);
        setUsernameAvailability(null);
        setCheckingUsername(false);

        if (usernameTimerRef.current) clearTimeout(usernameTimerRef.current);
        usernameTimerRef.current = setTimeout(() => {
          handleUsernameAvailability(cleaned);
        }, 1000);
        return;
      }

      if (name === "displayName") {
        // Keep user-typed spacing while editing; normalize on save.
        const cleaned = sanitizeProfileDisplayNameInput(value);
        setFormData((prev) => ({ ...prev, [name]: cleaned }));
        return;
      }

      if (name === "publicHandle") {
        const cleaned = trimAndLimitProfileText(value);
        setFormData((prev) => ({ ...prev, [name]: cleaned }));
        return;
      }

      setFormData((prev) => ({ ...prev, [name]: value }));
    },
    [updateUsernameFlags]
  );

  const handleUsernameAvailability = async (value) => {
    const cleaned = trimAndLimitProfileText(String(value || "").replace(/\s/g, ""));
    setCheckingUsername(true);
    try {
      const isAvailable = await checkUserNameAvailability(cleaned);
      setUsernameAvailability(isAvailable ? "available" : "taken");
    } catch (err) {
      console.error("Username availability error:", err);
      setUsernameAvailability("error");
    } finally {
      setCheckingUsername(false);
    }
  };

  const upsertAffiliateReward = useCallback((programKey, programName, memberId) => {
    setFormData((prev) => {
      const list = Array.isArray(prev.affiliatedRewards) ? [...prev.affiliatedRewards] : [];
      const idx = list.findIndex((r) => r.programKey === programKey);
      const item = { programKey, programName, memberId: memberId || "" };
      if (idx >= 0) list[idx] = item;
      else list.push(item);
      return { ...prev, affiliatedRewards: list };
    });
  }, []);

  const removeAffiliateReward = useCallback((programKey) => {
    setFormData((prev) => {
      const list = Array.isArray(prev.affiliatedRewards) ? prev.affiliatedRewards : [];
      return {
        ...prev,
        affiliatedRewards: list.filter((r) => r.programKey !== programKey),
      };
    });
  }, []);

  const saveProfile = useCallback(async () => {
    try {
      setSaveChangesLoading(true);
      const currentUser = getAuth().currentUser;
      if (!currentUser?.uid || !userProfile) {
        console.error("Missing currentUser or userProfile.");
        return;
      }

      if (formData.userName !== userProfile.userName) {
        await updateAzureUserProfileUsername(formData.userName, currentUser.email);
      }

      // Write ONLY the fields this form edits. Spreading the whole (possibly
      // stale) store profile here would clobber server-managed/separately-managed
      // fields — most importantly profileImage (owned by the avatar flow) — with
      // stale values. updateProfileInFirestore uses updateDoc, so omitted fields
      // are left untouched on the server.
      const editedFields = buildProfileEditPayload(formData);
      await updateProfileInFirestore(currentUser.uid, { ...editedFields, uid: currentUser.uid });
      // Local object for Azure sync + optimistic store update (keeps the store's
      // current profileImage; AuthUserProfileSync's onSnapshot reconciles next).
      const updated = { ...userProfile, ...editedFields, uid: currentUser.uid };

      if (azureUserProfile) {
        const nameParts = (updated?.displayName || "").trim().split(" ");
        const firstName = nameParts[0] || "";
        const lastName = nameParts.slice(1).join(" ") || "";

        const data = new FormData();
        data.append("FirstName", firstName);
        data.append("LastName", lastName);
        data.append("Biography", formData?.bio);
        data.append("AllowMatureContent", azureUserProfile?.allowMatureContent || false);
        data.append("SocialMedia.WebsiteAddress", azureUserProfile?.socialMedia?.websiteAddress || "");
        data.append("SocialMedia.TwitterHandle", azureUserProfile?.socialMedia?.twitterHandle || "");
        data.append("SocialMedia.FacebookProfile", azureUserProfile?.socialMedia?.facebookProfile || "");
        data.append("SocialMedia.InstagramProfile", azureUserProfile?.socialMedia?.instagramProfile || "");

        await updateAzureUserProfile(data);

        const updatedAzure = {
          ...azureUserProfile,
          userName: updated?.userName,
          fullName: updated?.displayName,
          biography: updated?.bio,
          emailAddress: updated?.email,
          phoneNumber: updated?.phoneNumber,
        };
        setAzureUserProfile(updatedAzure);
      }

      setUserProfile(updated);
      setSnackbarMessage("Profile updated successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setUserNameError(false);
      setUsernameAvailability(null);
      setUserNameDisabled(!updated.userName.includes("@"));
    } catch (error) {
      console.error("Error updating profile:", error);
      setSnackbarMessage("Error updating profile.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setSaveChangesLoading(false);
    }
  }, [formData, userProfile, azureUserProfile, setUserProfile]);

  const uploadAvatar = useCallback(
    async (uploadedFileOrUrl) => {
      const currentUser = getAuth().currentUser;
      if (!currentUser?.uid || !userProfile) return;

      let imageUrl = uploadedFileOrUrl;

      if (uploadedFileOrUrl instanceof Blob || uploadedFileOrUrl instanceof File) {
        console.error(
          "Uploader returned a Blob instead of URL. Adjust upload service to return a URL."
        );
        return;
      }

      if (typeof uploadedFileOrUrl === "string" && !uploadedFileOrUrl.startsWith("http")) {
        const { getDownloadURL, ref } = await import("firebase/storage");
        const { storage } = await import("../firebase");
        imageUrl = await getDownloadURL(ref(storage, uploadedFileOrUrl));
      }

      await persistProfileImageUpdate({
        currentUser,
        userProfile,
        imageUrl,
        updateProfileInFirestore,
        updateProfileImageInAllCollections,
        updateAzureUserProfilePicture,
        onProfilePersisted: (updated) => {
          setFormData((prev) => ({ ...prev, profileImage: imageUrl }));
          setUserProfile(updated);
        },
        onAzureSyncError: (error) => {
          console.warn("Failed to sync profile image to Azure:", error);
        },
      });
    },
    [userProfile, setUserProfile]
  );

  const removeAvatar = useCallback(async () => {
    const currentUser = getAuth().currentUser;
    if (!currentUser?.uid || !userProfile) return;

    const updated = { ...userProfile, profileImage: "", uid: currentUser.uid };
    setFormData((prev) => ({ ...prev, profileImage: "" }));
    await updateProfileInFirestore(currentUser.uid, updated);
    setUserProfile(updated);
    await updateProfileImageInAllCollections(currentUser.uid, "");
  }, [userProfile, setUserProfile]);

  /**
   * Safe sign out with optional redirect control.
   * signOutSafely({ redirect: false }) will skip window.location change,
   * which is necessary for the OTP handoff flow in PhoneOtpPage.
   */
  const signOutSafely = useCallback(
    async ({ redirect = true, redirectTo = "/", preserveAuthHandoff = false } = {}) => {
      try {
        try {
          sessionStorage.setItem(LOGGING_OUT_FLAG, "1");
        } catch {}

        if (redirect) {
          navigate(redirectTo, { replace: true, state: { clearFrom: true } });
        }

        const authInstance = getAuth();
        const currentUser = authInstance.currentUser;

        if (currentUser && navigator.onLine) {
          await removeFcmTokenFromFirestore(currentUser.uid);
        } else if (!navigator.onLine) {
          console.warn("User is offline. Skipping FCM token removal.");
        }

        try {
          if (currentUser) {
            const currentDeviceId = await getDeviceId();
            const userRef = doc(db, "users", currentUser.uid);
            const userSnap = await getDoc(userRef);
            const data = userSnap.exists() ? userSnap.data() || {} : {};
            const storedDeviceId = String(data?.lastSignedInDeviceId || "");

            // Only clear marker if this browser/device owns it.
            if (!storedDeviceId || storedDeviceId === currentDeviceId) {
              await updateDoc(userRef, {
                lastSignedInAt: deleteField(),
                lastSignedInDeviceId: deleteField(),
              });
            }
          }
        } catch (e) {
          console.warn("Failed to clear lastSignedIn marker on sign out:", e);
        }

        await authInstance.signOut();

        // Clear app state
        clearUserProfile();

        // Clear per-user session flags (success dialogs, etc.)
        clearUserSessionUiFlags();

        // Clear local cached profile/auth data and per-user browser state.
        try {
          clearSignedOutUserStorage({ preserveAuthHandoff });
        } catch (e) {
          console.warn("Failed to clear local profile/auth cache:", e);
        }
      } catch (err) {
        console.error("Error during logout:", err);
      } finally {
        setTimeout(() => {
          try {
            sessionStorage.removeItem(LOGGING_OUT_FLAG);
          } catch {}
        }, 500);
      }
    },
    [navigate, clearUserProfile, clearUserSessionUiFlags]
  );

  const snackbar = useMemo(
    () => ({ open: snackbarOpen, message: snackbarMessage, severity: snackbarSeverity }),
    [snackbarOpen, snackbarMessage, snackbarSeverity]
  );
  const closeSnackbar = useCallback(() => setSnackbarOpen(false), []);

  return {
    // data
    isLoading,
    user,
    userId,
    formData,
    profileTextMaxLength: PROFILE_TEXT_MAX_LENGTH,

    // form helpers
    setField,
    saveChangesLoading,

    // username UX
    userNameError,
    userNameDisabled,
    checkingUsername,
    usernameAvailability,
    handleUsernameAvailability,

    // affiliate rewards (from Admin Settings in Firestore)
    affiliatePrograms, // [{ key, label }]
    upsertAffiliateReward,
    removeAffiliateReward,

    // actions
    saveProfile,
    uploadAvatar,
    removeAvatar,
    signOutSafely,

    // snackbar
    snackbar,
    closeSnackbar,
  };
}
